import { http } from "@/lib/request";
import type { CoachListResult } from "@/types/api";

export interface CoachListParams {
  pageNum?: number;
  pageSize?: number;
  categoryId?: number;
  cityId?: number;
  keyword?: string;
  longitude?: number;
  latitude?: number;
  sortBy?: string;
}

export interface CoachFilterTagItem {
  id: number;
  name?: string;
  sort?: number;
}

export const coachApi = {
  getCoachList(params?: CoachListParams) {
    return http.get<CoachListResult>("/coach/lists", { params });
  },

  getFilterTags() {
    return http.get<CoachFilterTagItem[]>("/coach/filter-tags", {
      withAuth: false,
    });
  },

  getCoachDetail(id: number | string, location?: { longitude?: number; latitude?: number }) {
    return http.get<CoachDetailResult>("/coach/detail", {
      params: { id, ...location },
    });
  },

  getCoachMoments(id: number | string, limit = 20) {
    return http.get<CoachMomentListResult>("/coach/moments", {
      params: { id, limit },
    });
  },

  commentCoachMoment(momentId: number | string, content: string) {
    return http.post<CoachMomentComment>("/coach/moments/comment", { momentId, content });
  },

  deleteCoachMomentComment(commentId: number | string) {
    return http.delete<{ id: number; momentId?: number }>(`/coach/moments/comment/${commentId}`);
  },

  toggleCoachMomentLike(momentId: number | string) {
    return http.post<{ id: number; liked: boolean; likes: number }>("/coach/moments/like", { momentId });
  },
};

export interface CoachMomentComment {
  id: number;
  userId?: number;
  user?: string;
  avatar?: string;
  text?: string;
  date?: string;
  replyText?: string;
  replyDate?: string;
  canDelete?: boolean;
}

export interface CoachMomentItem {
  id: number;
  text?: string;
  time?: string;
  locationName?: string;
  likes?: number;
  liked?: boolean;
  views?: number;
  photos?: string[];
  hasVideo?: boolean;
  videoUrl?: string;
  videoCover?: string;
  comments?: CoachMomentComment[];
  commentCount?: number;
}

export interface CoachMomentListResult {
  lists?: CoachMomentItem[];
  count?: number;
}

export interface CoachDetailComment {
  id: number;
  comment?: string;
  service_comment?: number;
  create_time?: string;
  tags?: string[];
  virtual?: number;
  goods_comment_image?: string[];
  user?: {
    nickname?: string;
    avatar?: string;
  };
}

export interface CoachDetailReviewSummaryItem {
  label?: string;
  value?: number;
}

export interface CoachDetailReviewSummary {
  score?: number;
  items?: CoachDetailReviewSummaryItem[];
}

export interface CoachDetailGoodsItem {
  id: number;
  name?: string;
  image?: string;
  price?: number;
  scribing_price?: number;
  duration?: number;
  display_order_num?: number;
  marketing_tags?: string[];
}

export interface CoachDetailShop {
  id?: number;
  name?: string;
  logo?: string;
  address_detail?: string;
  distance?: number | string;
}

export interface CoachDetailResult {
  id: number;
  name?: string;
  gender?: number;
  age?: number;
  education?: string;
  nation?: string;
  skill_id?: number;
  intro_video?: string;
  work_photo?: string;
  life_photo?: string[];
  photos?: string[];
  introduction?: string;
  work_status?: number;
  order_num?: number;
  display_order_num?: number;
  good_comment?: string;
  score_comment?: number;
  display_rating?: number;
  review_summary?: CoachDetailReviewSummary;
  comment_num?: number;
  comment_lists?: CoachDetailComment[];
  goods_lists?: CoachDetailGoodsItem[];
  first_appoint?: string;
  distance?: number | string;
  distance_desc?: string;
  is_collect?: number;
  collect_num?: number;
  level?: string;
  operation_tags?: string[];
  operation_tag_ids?: number[];
  free_travel_fee?: number;
  free_travel_fee_distance_km?: number;
  specialties?: string[];
  health_certificate?: string;
  certification?: string;
  id_card?: string;
  shop?: CoachDetailShop;
  shop_name?: string;
}
